import { AutomationsFactory, CampaignsFactory, fetchRoute, FormsFactory, GenericWrapperContext, getBrandUrl, Icon, ListsFactory, Search, SendersFactory, SideMenu, SideMenuContainer, SubNav, TemplatesFactory } from "cakemail-ui-components-v2"
import { TSubNavItem } from "cakemail-ui-components-v2/dist/esm/components/subNav/types"
import { MouseEvent, useContext, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { ERoutes } from "../../utils/url"
import "./styles.scss"
import { MenuSettings } from "./MenuSettings"
import { TAppMenu, TAppMenuSearchResult, TAppMenuSearchResultItem } from "./types"
import { TSideMenuItemItem } from "cakemail-ui-components-v2/dist/esm/components/sideMenu/types"

export function AppMenu({ onLogout }: TAppMenu) {
    const genericWrapperContext = useContext(GenericWrapperContext)
    const [nav, setNav] = useState<string>("search") // home, add, search
    const { t } = useTranslation()
    const [search, setSearch] = useState<string>("")
    const [searchedResources, setSearchedResources] = useState<TAppMenuSearchResultItem[]>([])
    const [loading, setLoading] = useState<boolean>(false)

    useEffect(() => {
        if (search) {
            fetchSearchableResources()
        } else {
            setSearchedResources([])
        }
    }, [search])



    function fetchSearchableResources() {
        setLoading(true)
        Promise.allSettled([
            CampaignsFactory.list({
                filter: "name==*" + search + "*",
                page: 1,
                perPage: 25
            }),
            TemplatesFactory.list({
                filter: "name==*" + search + "*",
                page: 1,
                perPage: 25
            }),
            FormsFactory.list({
                filter: "name==*" + search + "*",
                page: 1,
                perPage: 25
            }),
            ListsFactory.list({
                filter: "name==*" + search + "*",
                page: 1,
                perPage: 25
            }),

            /*AutomationsFactory.list({
                filter: "name==*" + search + "*",
                page: 1,
                perPage: 25
            })*/
        ]).then(([campaigns, templates, forms, lists]) => {
            let items: TAppMenuSearchResultItem[] = []

            if (campaigns.status === "fulfilled") {
                items = items.concat(campaigns.value.data.map((campaign) => {
                    let status: any = campaign.status
                    if (status === "incomplete") status = t("general-email-incomplete")
                    else if (status === "delivering") status = t("general-email-delivering")
                    else if (status === "delivered") status = t("general-email-delivered")
                    else if (status === "scheduled") status = t("general-email-scheduled")
                    else status = t("general-email-campaign")

                    return {
                        name: campaign.name + "-" + campaign.id,
                        text: campaign.name,
                        support: status,
                        path: "#" + fetchRoute({ route: ERoutes.campaignEdit, params: { campaignId: campaign.id.toString() } }),
                        defaultItemIcon: false,
                    }
                }))
            }

            if (templates.status === "fulfilled") {
                items = items.concat(templates.value.data.map((template) => {
                    return {
                        name: template.name + "-" + template.id,
                        text: template.name,
                        support: t("general-email-template_one"),
                        path: "#" + fetchRoute({ route: ERoutes.templateEdit, params: { templateId: template.id.toString() } }),
                        defaultItemIcon: false,
                    }
                }))
            }

            if (forms.status === "fulfilled") {
                items = items.concat(forms.value.data.map((form) => {
                    return {
                        name: form.name + "-" + form.id,
                        text: form.name,
                        support: t("general-form_one"),
                        path: "#" + fetchRoute({ route: ERoutes.forms, params: { formId: form.id.toString() } }),
                        defaultItemIcon: false,
                    }
                }))
            }

            if (lists.status === "fulfilled") {
                items = items.concat(lists.value.data.map((list) => {
                    return {
                        name: list.name + "-" + list.id,
                        text: list.name,
                        support: t("general-list_one"),
                        path: "#" + fetchRoute({ route: ERoutes.lists }) + "?listId=" + list.id.toString(),
                        defaultItemIcon: false,
                    }
                }))
            }

            setSearchedResources(items)
            setLoading(false)
        })
    }
    function handleNavClick(event: MouseEvent<HTMLButtonElement>, item: TSubNavItem) {
        setNav(item.name)
    }

    function handleSearchChange(cSearch: string) {
        setSearch(cSearch)
    }


    return <SideMenu
        className="appMenu"
        logoUrl={getBrandUrl({ brand: genericWrapperContext?.partnerBrand, elKey: "logo" })}
        canMinify
    >
        <SubNav
            initialSelected={nav}
            menuVariant='inherit'
            btnOverride={{
                color: "secondary",
                variant: "contained"
            }}
            onClick={handleNavClick}
            items={[
                {
                    name: "home",
                    text: <Icon name="Home18" />
                },
                {
                    name: "add",
                    text: <Icon name="Add18" />
                },
                {
                    name: "search",
                    text: <Icon name="Search18" />
                },
            ]}
        />

        {nav === "home" &&
            <SideMenuContainer
                menuItems={[
                    {
                        icon: "Dashboard18",
                        text: t("general-home"),
                        name: "dashboard",
                        path: "#" + fetchRoute({ route: ERoutes.dashboard }),
                        defaultItemIcon: false,
                    },
                    {
                        icon: "Campaigns18",
                        text: t("general-campaigns"),
                        name: "campaigns",
                        subItems: [
                            {
                                text: t("general-regular"),
                                name: "regularCampaigns",
                                path: "#" + fetchRoute({ route: ERoutes.campaigns }),
                                defaultItemIcon: false,
                            },
                            {
                                text: t("general-abtest"),
                                name: "abtestCampaigns",
                                path: "#" + fetchRoute({ route: ERoutes.campaignsABTest }),
                                defaultItemIcon: false,
                            },
                            {
                                text: t("general-browse-email-templates"),
                                name: "templateBrowse",
                                path: "#" + fetchRoute({ route: ERoutes.templateBrowse }),
                                defaultItemIcon: false,
                            },
                        ]
                    },
                    {
                        icon: "Flow18",
                        text: t("general-automations"),
                        name: "automations",
                        subItems: [
                            {
                                text: t("general-drip-campaigns"),
                                name: "dripCampaigns",
                                path: "#" + fetchRoute({ route: ERoutes.automations }),
                                defaultItemIcon: false,
                            },
                        ]
                    },
                    {
                        icon: "Field18",
                        text: t("general-forms-pages"),
                        name: "forms-pages",
                        subItems: [
                            {
                                text: t("general-form_other"),
                                name: "forms",
                                path: "#" + fetchRoute({ route: ERoutes.forms }),
                                defaultItemIcon: false,
                            },
                        ]
                    },
                    {
                        icon: "User18",
                        text: t("general-audience"),
                        name: "audience",
                        subItems: [
                            {
                                text: t("general-all-contacts"),
                                name: "allContacts",
                                path: "#" + fetchRoute({ route: ERoutes.lists }),
                                defaultItemIcon: false,
                            },
                        ]
                    },
                    {
                        icon: "Code18",
                        text: t("general-email-api"),
                        name: "email-api",
                        subItems: [
                            {
                                name: "metrics",
                                path: "#" + fetchRoute({ route: ERoutes.emailMetrics }),
                                text: t("general-metrics-trends"),
                                defaultItemIcon: false,
                            },
                            {
                                name: "deliverability",
                                path: "#" + fetchRoute({ route: ERoutes.emailDeliverability }),
                                text: t("general-deliverability"),
                                defaultItemIcon: false,
                            },
                            /*{
                                name: "reports",
                                path: "#" + fetchRoute({ route: ERoutes.emailReports }),
                                text: t("email-api-header-reports-heading"),
                                icon: "Sends24",
                            },*/
                            {
                                name: "requestLogs",
                                path: "#" + fetchRoute({ route: ERoutes.emailRequestLog }),
                                text: t("general-requestLog"),
                                defaultItemIcon: false,
                            },
                            {
                                name: "doc-started",
                                path: "https://cakemail.dev/api/reference/email-api#submit-an-email",
                                text: t("general-getting-started"),
                                defaultItemIcon: "Link10"
                            },
                            {
                                name: "doc-activity",
                                path: "https://cakemail.dev/docs/getting-started-with-the-email-api",
                                text: t("general-api-references"),
                                defaultItemIcon: "Link10"
                            },
                        ]
                    },

                ]}
            />
        }
        {nav === "add" &&
            <SideMenuContainer
                menuItemClick={(event) => {
                    debugger
                    console.log(event.currentTarget.getAttribute("data-name"))
                    //CampaignsFactory.create()
                }}
                menuItems={[
                    {
                        text: t("general-campaign"),
                        variant: "subSection",
                        name: "campaignsCreate",
                    },

                ]}
            />
        }

        {nav === "search" &&
            <>
                <Search
                    onChange={handleSearchChange}
                    placeholder={t("general-search-resources-placeholder")}
                    initValue={search}
                    className={loading ? "loading" : ""}
                />
                <SideMenuContainer
                    className={loading ? "loading" : ""}
                    forcedLocationPath="/campaigns/draft"
                    menuItems={searchedResources}
                    menuItemClick={(event) => {
                        console.log(event.currentTarget.getAttribute("data-name"))
                    }}
                />
            </>
        }

        <MenuSettings onGearClick={() => { }} onLogout={onLogout} />

    </SideMenu >
}